/**
 * WebRTC Softphone Component - Full Featured Browser-Based Phone
 * ==============================================================
 * Complete softphone with WebRTC integration for Zadarma PBX
 *
 * Features:
 * - Real browser-based calling via WebRTC/SIP
 * - Incoming call notifications with caller ID lookup
 * - Automatic customer card popup on incoming calls
 * - Click-to-call from anywhere in the system
 * - DTMF tone sending
 * - Call recording indicator
 * - Call history and quick dial
 * - Extension-based routing
 */

import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import ReactDOM from 'react-dom';
import JsSIP from 'jssip';
import { io } from 'socket.io-client';
import { TELEPHONY_URL, API_BASE as TELEPHONY_API, SOCKET_PATH } from './config';
import config from '../../config/config';
import { getSessionToken } from '../../utils/session';
import QSISEmotionAnalyzer, { CallTypeSelector } from './QSISEmotionAnalyzer';

const API_BASE = config.api.baseUrl;

// The browser softphone's SIP registration (see the credentials effect below). Off by default.
export const SOFTPHONE_ENABLED = process.env.REACT_APP_SOFTPHONE_ENABLED === 'true';

// Call states
export const CALL_STATE = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  RINGING: 'ringing',
  INCOMING: 'incoming',
  ACTIVE: 'active',
  HOLDING: 'holding',
  TRANSFERRING: 'transferring',
  ENDED: 'ended'
};

// SIP registration states
export const REG_STATE = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  REGISTERED: 'registered',
  FAILED: 'failed'
};

// Our Asterisk WebSocket server (via nginx proxy on port 443)
const SIP_SERVERS = [
  { wss: 'wss://pbx.afikim.my/ws', domain: 'pbx.afikim.my' }
];

// Context for global access to softphone
const SoftphoneContext = createContext(null);

export const useSoftphone = () => useContext(SoftphoneContext);

/**
 * WebRTC Softphone Provider - Provides softphone functionality to entire app
 */
export const SoftphoneProvider = ({ children, currentUser }) => {
  const [regState, setRegState] = useState(REG_STATE.DISCONNECTED);
  const [callState, setCallState] = useState(CALL_STATE.IDLE);
  const [callInfo, setCallInfo] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [error, setError] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [sipCredentials, setSipCredentials] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [pendingNumber, setPendingNumber] = useState(null);
  const [socket, setSocket] = useState(null);
  const [matchedCustomer, setMatchedCustomer] = useState(null); // Customer found by phone search
  const [leadSource, setLeadSource] = useState(null); // Source from shadow number
  const [ivrChoice, setIvrChoice] = useState(null); // IVR selection (1=service, 2=sales)
  const [isReturningCaller, setIsReturningCaller] = useState(false); // Is this a returning caller
  const [existingCustomer, setExistingCustomer] = useState(null); // Existing customer info from CRM
  const [callType, setCallType] = useState('service'); // 'service' or 'sales' - QSIS emotion analysis mode
  const [currentEmotion, setCurrentEmotion] = useState(null); // Current emotion data from QSIS
  const [currentTip, setCurrentTip] = useState(null); // Current coaching tip from QSIS

  const uaRef = useRef(null);
  const sessionRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const callTimerRef = useRef(null);
  const callStartTimeRef = useRef(null);
  const serverIndexRef = useRef(0);
  const ringtoneRef = useRef(null);
  const connectionAttemptsRef = useRef(0);
  const maxConnectionAttempts = 3;

  // QSIS Emotion tracking refs for transcription
  const emotionDataRef = useRef({
    startEmotion: null,
    endEmotion: null,
    transcriptSegments: [],
    fullTranscript: '',
    positiveCount: 0,
    negativeCount: 0,
    neutralCount: 0,
    coachingTips: []
  });

  // Initialize audio element for remote audio
  useEffect(() => {
    remoteAudioRef.current = new Audio();
    remoteAudioRef.current.autoplay = true;

    return () => {
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = null;
      }
    };
  }, []);

  // Load SIP credentials for current user from our Asterisk server.
  // OFF unless REACT_APP_SOFTPHONE_ENABLED=true (2026-10-15): the softphone UI is not rendered,
  // /webrtc/credentials does not exist on the telephony service, and the fallback hands every
  // logged-in browser extension 100's SIP password and registers it at pbx.afikim.my — a host
  // under the old domain. It only "worked" while config.js pointed every request at an unreachable
  // :3010; with the address fixed it would start doing exactly that.
  useEffect(() => {
    const loadCredentials = async () => {
      if (!SOFTPHONE_ENABLED) return;
      if (!currentUser?.email) return;

      try {
        // Get WebRTC credentials from our Asterisk API
        // Encode name to handle Hebrew/Unicode characters
        const userName = currentUser.name || currentUser.email.split('@')[0];
        const res = await fetch(`${TELEPHONY_API}/webrtc/credentials`, {
          headers: {
            'x-user-email': currentUser.email,
            'x-user-name': encodeURIComponent(userName)
          }
        });

        if (res.ok) {
          const data = await res.json();
          if (data.success && data.data) {
            setSipCredentials({
              sip_username: data.data.username,
              sip_password: data.data.password,
              extension: data.data.extension,
              server: data.data.server,
              wss_url: data.data.wss_url,
              domain: data.data.domain
            });
            console.log('[Softphone] Loaded Asterisk credentials for extension:', data.data.extension);
            return;
          }
        }

        // Fallback: the caller's OWN extension, resolved by the server from the session. It used
        // to fetch extension 100 for everyone (2026-10-19).
        const fallbackRes = await fetch(`${TELEPHONY_API}/my/sip-credentials`);
        const fallbackData = await fallbackRes.json().catch(() => ({}));
        if (fallbackRes.ok && fallbackData.success && fallbackData.credentials) {
          setSipCredentials({ ...fallbackData.credentials, extension: String(fallbackData.credentials.extension_number) });
        } else if (fallbackRes.status === 404) {
          setError(fallbackData.error || 'לא הוגדרה לך שלוחה');
        }
      } catch (err) {
        console.error('[Softphone] Error loading Asterisk credentials:', err);
        setError('Could not load SIP credentials');
      }
    };

    loadCredentials();
  }, [currentUser?.email]);

  // Connect to telephony socket.io for lead/IVR info
  useEffect(() => {
    // Import socket.io dynamically
    const connectTelephonySocket = async () => {
      try {
        const { io } = await import('socket.io-client');
        const telephonySocket = io(TELEPHONY_API.replace('/api/telephony', ''), {
          path: '/api/telephony/socket.io',
          auth: { token: getSessionToken() || '' },   // the telephony service admits logged-in users only
          transports: ['websocket', 'polling']
        });

        telephonySocket.on('connect', () => {
          console.log('[Softphone] Connected to telephony socket');
        });

        // Listen for incoming call info (includes source and returning status)
        telephonySocket.on('call:incoming', (data) => {
          console.log('[Softphone] Received call info from server:', data);
          if (data.lead_source) {
            setLeadSource(data.lead_source);
          }
          if (data.is_returning) {
            setIsReturningCaller(true);
          }
          if (data.customer) {
            setExistingCustomer(data.customer);
          }
        });

        // Listen for IVR choice updates
        telephonySocket.on('lead:ivr_updated', (data) => {
          console.log('[Softphone] IVR choice updated:', data);
          if (data.ivr_choice) {
            setIvrChoice(data.ivr_choice);
          }
        });

        setSocket(telephonySocket);

        return () => {
          telephonySocket.disconnect();
        };
      } catch (err) {
        console.error('[Softphone] Error connecting to telephony socket:', err);
      }
    };

    connectTelephonySocket();
  }, []);

  // Connect to SIP server
  const connect = useCallback(() => {
    if (!sipCredentials?.sip_username || !sipCredentials?.sip_password) {
      setError('Missing SIP credentials');
      return;
    }

    if (uaRef.current) {
      uaRef.current.stop();
    }

    try {
      setRegState(REG_STATE.CONNECTING);
      setError(null);

      const server = SIP_SERVERS[serverIndexRef.current % SIP_SERVERS.length];
      console.log('[Softphone] Connecting to:', server.wss);

      const socket = new JsSIP.WebSocketInterface(server.wss);

      const configuration = {
        sockets: [socket],
        uri: `sip:${sipCredentials.sip_username}@${server.domain}`,
        password: sipCredentials.sip_password,
        display_name: currentUser?.name || sipCredentials.sip_username,
        register: true,
        session_timers: false,
        contact_uri: `sip:${sipCredentials.sip_username}@${server.domain};transport=ws`,
        register_expires: 300
      };

      const ua = new JsSIP.UA(configuration);

      ua.on('connected', () => {
        console.log('[Softphone] WebSocket connected');
      });

      ua.on('disconnected', () => {
        console.log('[Softphone] WebSocket disconnected');
        setRegState(REG_STATE.DISCONNECTED);
      });

      ua.on('registered', () => {
        console.log('[Softphone] SIP Registered!');
        setRegState(REG_STATE.REGISTERED);
        setError(null);
        serverIndexRef.current = 0;
      });

      ua.on('unregistered', () => {
        console.log('[Softphone] Unregistered');
        setRegState(REG_STATE.DISCONNECTED);
      });

      ua.on('registrationFailed', (e) => {
        console.error('[Softphone] Registration failed:', e.cause);
        serverIndexRef.current++;
        if (serverIndexRef.current < SIP_SERVERS.length) {
          console.log('[Softphone] Trying next server...');
          ua.stop();
          setTimeout(() => connect(), 1000);
        } else {
          setRegState(REG_STATE.FAILED);
          setError(`Registration failed: ${e.cause}`);
          serverIndexRef.current = 0;
        }
      });

      ua.on('newRTCSession', (e) => {
        handleNewSession(e.session, e.originator);
      });

      ua.start();
      uaRef.current = ua;

    } catch (err) {
      console.error('[Softphone] Connection error:', err);
      setError(err.message);
      setRegState(REG_STATE.FAILED);
    }
  }, [sipCredentials, currentUser]);

  // Auto-connect when credentials are available (with retry limit)
  useEffect(() => {
    if (sipCredentials && regState === REG_STATE.DISCONNECTED) {
      if (connectionAttemptsRef.current < maxConnectionAttempts) {
        connectionAttemptsRef.current++;
        console.log(`[Softphone] Connection attempt ${connectionAttemptsRef.current}/${maxConnectionAttempts}`);
        const timer = setTimeout(() => connect(), 2000 * connectionAttemptsRef.current);
        return () => clearTimeout(timer);
      } else {
        console.log('[Softphone] Max connection attempts reached, stopping auto-reconnect');
        setError('Unable to connect to phone server. Please refresh the page to try again.');
      }
    } else if (regState === REG_STATE.REGISTERED) {
      // Reset counter on successful connection
      connectionAttemptsRef.current = 0;
    }
  }, [sipCredentials, regState, connect]);

  // Handle new SIP session (incoming or outgoing)
  const handleNewSession = useCallback((session, originator) => {
    console.log('[Softphone] New session! originator:', originator, 'direction:', session.direction);
    sessionRef.current = session;

    const remoteNumber = session.remote_identity?.uri?.user || 'Unknown';
    const isIncoming = originator === 'remote';
    console.log('[Softphone] isIncoming:', isIncoming, 'remoteNumber:', remoteNumber);

    setCallInfo({
      direction: isIncoming ? 'incoming' : 'outgoing',
      remoteNumber,
      startTime: null,
      callId: session.id
    });

    if (isIncoming) {
      console.log('[Softphone] INCOMING CALL from:', remoteNumber);
      setCallState(CALL_STATE.INCOMING);
      setIsOpen(true);
      setIsMinimized(false);
      playRingtone();
      lookupCustomer(remoteNumber);

      // Notify via socket
      if (socket) {
        socket.emit('incoming_call', { phone: remoteNumber, callId: session.id });
      }
    } else {
      setCallState(CALL_STATE.CONNECTING);
    }

    // Session events
    session.on('progress', () => {
      console.log('[Softphone] Call progress (ringing)');
      setCallState(CALL_STATE.RINGING);
    });

    session.on('accepted', () => {
      console.log('[Softphone] Call accepted');
      stopRingtone();
      setCallState(CALL_STATE.ACTIVE);
      startCallTimer();

      // Get the RTCPeerConnection and attach remote audio
      const pc = session.connection;
      if (pc) {
        console.log('[Softphone] Got RTCPeerConnection, state:', pc.connectionState);

        // Get remote streams
        const receivers = pc.getReceivers();
        console.log('[Softphone] Receivers:', receivers.length);

        receivers.forEach((receiver, i) => {
          if (receiver.track && receiver.track.kind === 'audio') {
            console.log('[Softphone] Found remote audio track');
            const stream = new MediaStream([receiver.track]);
            if (remoteAudioRef.current) {
              remoteAudioRef.current.srcObject = stream;
              remoteAudioRef.current.play().then(() => {
                console.log('[Softphone] Remote audio playing!');
              }).catch(err => {
                console.error('[Softphone] Play failed:', err);
              });
            }
          }
        });
      }
    });

    session.on('confirmed', () => {
      console.log('[Softphone] Call confirmed');
      setCallState(CALL_STATE.ACTIVE);
    });

    session.on('ended', (e) => {
      console.log('[Softphone] Call ended:', e.cause);
      handleCallEnded();
    });

    session.on('failed', (e) => {
      console.log('[Softphone] Call failed:', e.cause, 'originator:', e.originator);
      console.log('[Softphone] Full failure event:', JSON.stringify({
        cause: e.cause,
        originator: e.originator,
        message: e.message?.status_code,
        reason: e.message?.reason_phrase
      }));
      setError(`Call failed: ${e.cause}`);
      handleCallEnded();
    });

    // Handle remote audio stream
    session.on('peerconnection', (e) => {
      const pc = e.peerconnection;

      // Debug ICE connection
      pc.oniceconnectionstatechange = () => {
        console.log('[Softphone] ICE connection state:', pc.iceConnectionState);
      };

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          console.log('[Softphone] ICE candidate:', event.candidate.type, event.candidate.address);
        }
      };

      // Debug local tracks
      pc.getSenders().forEach((sender, i) => {
        if (sender.track) {
          console.log('[Softphone] Local track', i, ':', sender.track.kind, 'enabled:', sender.track.enabled, 'muted:', sender.track.muted);
        }
      });

      pc.ontrack = (event) => {
        console.log('[Softphone] Remote track received:', event.track.kind);
        if (remoteAudioRef.current && event.streams[0]) {
          remoteAudioRef.current.srcObject = event.streams[0];
          console.log('[Softphone] Remote audio attached to element');
          // Force play for Chrome autoplay policy
          remoteAudioRef.current.play().then(() => {
            console.log('[Softphone] Remote audio playing');
          }).catch(err => {
            console.error('[Softphone] Remote audio play failed:', err);
          });
        }
      };
    });
  }, [socket]);

  // Call timer
  const startCallTimer = useCallback(() => {
    callStartTimeRef.current = Date.now();
    callTimerRef.current = setInterval(() => {
      const duration = Math.floor((Date.now() - callStartTimeRef.current) / 1000);
      setCallDuration(duration);
    }, 1000);
  }, []);

  const stopCallTimer = useCallback(() => {
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
    callStartTimeRef.current = null;
    setCallDuration(0);
  }, []);

  // Save call transcription to database
  const saveCallTranscription = useCallback(async (callInfoData, duration) => {
    try {
      const emotionData = emotionDataRef.current;
      const transcriptionData = {
        phone_number: callInfoData?.remoteNumber || '',
        call_start_time: callStartTimeRef.current?.toISOString() || new Date().toISOString(),
        call_end_time: new Date().toISOString(),
        duration_seconds: duration || callDuration,
        call_direction: callInfoData?.direction || 'inbound',
        call_type: callType,
        customer_emotion_start: emotionData.startEmotion?.dominant || 'neutral',
        customer_emotion_end: emotionData.endEmotion?.dominant || 'neutral',
        customer_emotion_start_confidence: emotionData.startEmotion?.confidence || 0.5,
        customer_emotion_end_confidence: emotionData.endEmotion?.confidence || 0.5,
        agent_id: currentUser?.id || null,
        agent_name: currentUser?.name || currentUser?.email || 'Unknown',
        agent_extension: sipCredentials?.extension || null,
        positive_count: emotionData.positiveCount || 0,
        negative_count: emotionData.negativeCount || 0,
        neutral_count: emotionData.neutralCount || 0,
        transcription_text: emotionData.fullTranscript || '',
        transcription_segments: emotionData.transcriptSegments || [],
        coaching_tips: emotionData.coachingTips || [],
        // Calculate overall score based on emotion improvement
        overall_score: calculateOverallScore(emotionData),
        // Link to customer if we found one
        account_id: matchedCustomer?.type === 'Account' ? matchedCustomer.id : null,
        contact_id: matchedCustomer?.type === 'Contact' ? matchedCustomer.id : null,
        lead_id: matchedCustomer?.type === 'Lead' ? matchedCustomer.id : null,
        created_by_id: currentUser?.id || null
      };

      const response = await fetch(`${TELEPHONY_API}/transcriptions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(transcriptionData)
      });

      const result = await response.json();
      if (result.success) {
        console.log('[Softphone] Call transcription saved:', result.data.id);
      } else {
        console.error('[Softphone] Failed to save transcription:', result.error);
      }
    } catch (err) {
      console.error('[Softphone] Error saving transcription:', err);
    }
  }, [callType, callDuration, currentUser, sipCredentials, matchedCustomer]);

  // Calculate overall score based on emotion data
  const calculateOverallScore = (emotionData) => {
    let score = 50; // Base score

    // Positive ending adds points
    if (emotionData.endEmotion?.dominant === 'happy') score += 30;
    else if (emotionData.endEmotion?.dominant === 'excited') score += 25;
    else if (emotionData.endEmotion?.dominant === 'neutral') score += 10;
    else if (emotionData.endEmotion?.dominant === 'sad') score -= 10;
    else if (emotionData.endEmotion?.dominant === 'angry') score -= 25;

    // Emotion improvement adds points
    const emotionRank = { angry: 0, sad: 1, fear: 2, neutral: 3, happy: 4, excited: 5 };
    const startRank = emotionRank[emotionData.startEmotion?.dominant] || 3;
    const endRank = emotionRank[emotionData.endEmotion?.dominant] || 3;
    score += (endRank - startRank) * 5;

    // Positive/Negative ratio
    const total = emotionData.positiveCount + emotionData.negativeCount + emotionData.neutralCount;
    if (total > 0) {
      const positiveRatio = emotionData.positiveCount / total;
      score += Math.round(positiveRatio * 20);
    }

    return Math.max(0, Math.min(100, score));
  };

  // Handle call ended
  const handleCallEnded = useCallback(() => {
    // Save transcription before resetting state
    if (callInfo && callDuration > 5) {
      saveCallTranscription(callInfo, callDuration);
    }

    stopRingtone();
    stopCallTimer();
    sessionRef.current = null;
    setCallState(CALL_STATE.IDLE);
    setCallInfo(null);
    setCustomerInfo(null);
    setIsMuted(false);
    setIsRecording(false);
    // Reset lead info
    setLeadSource(null);
    setIvrChoice(null);
    setIsReturningCaller(false);
    setExistingCustomer(null);
    // Reset emotion tracking
    setCurrentEmotion(null);
    setCurrentTip(null);
    emotionDataRef.current = {
      startEmotion: null,
      endEmotion: null,
      transcriptSegments: [],
      fullTranscript: '',
      positiveCount: 0,
      negativeCount: 0,
      neutralCount: 0,
      coachingTips: []
    };

    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }
  }, [stopCallTimer, callInfo, callDuration, saveCallTranscription]);

  // Play/stop ringtone
  const playRingtone = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.type = 'sine';
      oscillator.frequency.value = 440;
      gainNode.gain.value = 0.3;

      ringtoneRef.current = { audioContext, oscillator, gainNode };

      const ring = () => {
        if (!ringtoneRef.current) return;
        oscillator.start();
        setTimeout(() => {
          if (ringtoneRef.current) {
            oscillator.stop();
          }
        }, 400);
      };

      ring();
    } catch (e) {
      console.log('[Softphone] Ringtone error:', e);
    }
  }, []);

  const stopRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      try {
        ringtoneRef.current.oscillator?.stop();
        ringtoneRef.current.audioContext?.close();
      } catch (e) {}
      ringtoneRef.current = null;
    }
  }, []);

  // Lookup customer by phone number - searches Phone, PersonMobilePhone, Another_phone__c
  const lookupCustomer = useCallback(async (phoneNumber) => {
    if (!phoneNumber) return;

    // Clean phone - extract only digits
    let cleanPhone = phoneNumber.replace(/\D/g, '');
    // Remove 972 prefix if present
    if (cleanPhone.startsWith('972')) {
      cleanPhone = '0' + cleanPhone.substring(3);
    }
    // Keep last 9-10 digits for search
    if (cleanPhone.length > 10) {
      cleanPhone = cleanPhone.slice(-10);
    }
    if (cleanPhone.length < 7) return;

    console.log('[Softphone] Looking up customer for phone:', cleanPhone);

    try {
      // Use the db search API - searches Phone, PersonMobilePhone, Another_phone__c
      const res = await fetch(`${API_BASE}/api/db/search/phone?phone=${encodeURIComponent(cleanPhone)}&limit=10`);
      if (!res.ok) {
        console.log('[Softphone] Phone search API failed, status:', res.status);
        return;
      }

      const data = await res.json();
      console.log('[Softphone] Phone search result:', data);

      // Collect all matches
      const allMatches = [];

      // Add Accounts
      if (data.results?.Account?.length > 0) {
        data.results.Account.forEach(account => {
          allMatches.push({
            id: account.Id,
            name: account.Name,
            phone: account.Phone || account.PersonMobilePhone || account.Another_phone__c,
            type: 'Account',
            company: account.Name
          });
        });
      }

      // Add Contacts
      if (data.results?.Contact?.length > 0) {
        data.results.Contact.forEach(contact => {
          allMatches.push({
            id: contact.Id,
            name: `${contact.FirstName || ''} ${contact.LastName || ''}`.trim(),
            phone: contact.Phone || contact.MobilePhone,
            type: 'Contact',
            accountId: contact.AccountId,
            company: contact.Account?.Name || ''
          });
        });
      }

      console.log('[Softphone] Total matches found:', allMatches.length);

      if (allMatches.length === 0) {
        console.log('[Softphone] No customer found for phone:', cleanPhone);
        setMatchedCustomer(null);
        return;
      }

      if (allMatches.length === 1) {
        // Single match - auto-select and open
        const customer = allMatches[0];
        setCustomerInfo(customer);
        setMatchedCustomer(customer);
        openCustomerCard(customer);
      } else {
        // Multiple matches - store for selection in popup
        setCustomerInfo(null);
        setMatchedCustomer({ multiple: true, matches: allMatches });
      }
    } catch (err) {
      console.error('[Softphone] Customer lookup error:', err);
    }
  }, []);

  // Open customer card function
  const openCustomerCard = useCallback((customer) => {
    if (!customer) return;

    const navigateToId = customer.type === 'Account'
      ? customer.id
      : (customer.accountId || customer.id);

    if (navigateToId) {
      const targetUrl = `/objects/Account/records/${navigateToId}`;
      console.log('[Softphone] Opening customer card:', targetUrl);

      // Broadcast to open customer card in all tabs
      if (callChannel) {
        callChannel.postMessage({
          type: 'open-customer-card',
          customerId: navigateToId,
          customerName: customer.name
        });
      }

      // Navigate in current window
      window.location.hash = `#${targetUrl}`;
    }
  }, []);

  // Make outgoing call
  const makeCall = useCallback((phoneNumber) => {
    if (!phoneNumber) return;

    if (regState !== REG_STATE.REGISTERED) {
      setError('Not registered to SIP server');
      return;
    }

    if (callState !== CALL_STATE.IDLE) {
      setError('Already in a call');
      return;
    }

    // Format Israeli number
    let cleanNumber = phoneNumber.replace(/\D/g, '');
    if (cleanNumber.startsWith('0')) {
      cleanNumber = '972' + cleanNumber.slice(1);
    } else if (cleanNumber.length === 9 && !cleanNumber.startsWith('972')) {
      cleanNumber = '972' + cleanNumber;
    }

    try {
      const server = SIP_SERVERS[0];
      const target = `sip:${cleanNumber}@${server.domain}`;

      console.log('[Softphone] Calling:', target);

      const options = {
        mediaConstraints: { audio: true, video: false },
        pcConfig: {
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' },
            // TURN server for NAT traversal (TCP and UDP on port 3478)
            {
              urls: ['turn:161.97.142.207:3478', 'turn:161.97.142.207:3478?transport=tcp'],
              username: 'afikim',
              credential: 'Afikim2026Turn!'
            }
          ],
          iceTransportPolicy: 'all'
        }
      };

      uaRef.current.call(target, options);
      setCallState(CALL_STATE.CONNECTING);
      setIsOpen(true);
      setIsMinimized(false);

      // Lookup customer info
      lookupCustomer(phoneNumber);

    } catch (err) {
      console.error('[Softphone] Call error:', err);
      setError(err.message);
    }
  }, [regState, callState, lookupCustomer]);

  // Answer incoming call
  const answerCall = useCallback(() => {
    console.log('[Softphone] answerCall called, state:', callState, 'session:', !!sessionRef.current);

    // Allow answering in both INCOMING and RINGING states
    const canAnswer = callState === CALL_STATE.INCOMING || callState === CALL_STATE.RINGING;
    if (!sessionRef.current || !canAnswer) {
      console.log('[Softphone] Cannot answer - no session or wrong state');
      return;
    }

    try {
      stopRingtone();

      const options = {
        mediaConstraints: { audio: true, video: false },
        pcConfig: {
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' },
            // TURN server for NAT traversal (TCP and UDP on port 3478)
            {
              urls: ['turn:161.97.142.207:3478', 'turn:161.97.142.207:3478?transport=tcp'],
              username: 'afikim',
              credential: 'Afikim2026Turn!'
            }
          ],
          iceTransportPolicy: 'all'
        }
      };

      console.log('[Softphone] Answering call with TURN servers...');
      sessionRef.current.answer(options);
      // Don't set ACTIVE or start timer here - wait for 'accepted' event
      // This ensures the call is truly connected before showing active state
      setCallState(CALL_STATE.CONNECTING);

    } catch (err) {
      console.error('[Softphone] Answer error:', err);
      setError(err.message);
    }
  }, [callState, stopRingtone]);

  // Reject incoming call
  const rejectCall = useCallback(() => {
    console.log('[Softphone] rejectCall called, state:', callState, 'session:', !!sessionRef.current);

    // Allow rejecting in both INCOMING and RINGING states
    const canReject = callState === CALL_STATE.INCOMING || callState === CALL_STATE.RINGING;
    if (!sessionRef.current || !canReject) {
      console.log('[Softphone] Cannot reject - no session or wrong state');
      return;
    }

    try {
      stopRingtone();
      sessionRef.current.terminate({
        status_code: 486,
        reason_phrase: 'Busy Here'
      });
      console.log('[Softphone] Call rejected');
    } catch (err) {
      console.error('[Softphone] Reject error:', err);
    }
    handleCallEnded();
  }, [callState, stopRingtone, handleCallEnded]);

  // Hang up call
  const hangup = useCallback(() => {
    if (sessionRef.current) {
      try {
        sessionRef.current.terminate();
      } catch (err) {
        console.error('[Softphone] Hangup error:', err);
      }
    }
    handleCallEnded();
  }, [handleCallEnded]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (!sessionRef.current) return;

    try {
      if (isMuted) {
        sessionRef.current.unmute({ audio: true });
      } else {
        sessionRef.current.mute({ audio: true });
      }
      setIsMuted(!isMuted);
    } catch (err) {
      console.error('[Softphone] Mute error:', err);
    }
  }, [isMuted]);

  // Toggle hold
  const toggleHold = useCallback(() => {
    if (!sessionRef.current) return;

    try {
      if (callState === CALL_STATE.HOLDING) {
        sessionRef.current.unhold();
        setCallState(CALL_STATE.ACTIVE);
      } else if (callState === CALL_STATE.ACTIVE) {
        sessionRef.current.hold();
        setCallState(CALL_STATE.HOLDING);
      }
    } catch (err) {
      console.error('[Softphone] Hold error:', err);
    }
  }, [callState]);

  // Send DTMF
  const sendDTMF = useCallback((digit) => {
    if (!sessionRef.current || callState !== CALL_STATE.ACTIVE) return;

    try {
      sessionRef.current.sendDTMF(digit);
    } catch (err) {
      console.error('[Softphone] DTMF error:', err);
    }
  }, [callState]);

  // Blind transfer
  const blindTransfer = useCallback((targetNumber) => {
    if (!sessionRef.current || callState !== CALL_STATE.ACTIVE) return;

    try {
      const server = SIP_SERVERS[0];
      const target = `sip:${targetNumber}@${server.domain}`;
      sessionRef.current.refer(target);
      setCallState(CALL_STATE.TRANSFERRING);
    } catch (err) {
      console.error('[Softphone] Transfer error:', err);
      setError(err.message);
    }
  }, [callState]);

  // Open softphone with number to dial
  const openWithNumber = useCallback((phoneNumber) => {
    setPendingNumber(phoneNumber);
    setIsOpen(true);
    setIsMinimized(false);

    // If registered and idle, auto-dial
    if (regState === REG_STATE.REGISTERED && callState === CALL_STATE.IDLE) {
      setTimeout(() => makeCall(phoneNumber), 100);
    }
  }, [regState, callState, makeCall]);

  // Disconnect from SIP
  const disconnect = useCallback(() => {
    if (uaRef.current) {
      uaRef.current.stop();
      uaRef.current = null;
    }
    setRegState(REG_STATE.DISCONNECTED);
    handleCallEnded();
  }, [handleCallEnded]);

  // Context value
  const contextValue = {
    regState,
    callState,
    callInfo,
    customerInfo,
    matchedCustomer,
    error,
    isMuted,
    isRecording,
    callDuration,
    sipCredentials,
    isOpen,
    isMinimized,
    pendingNumber,
    leadSource,       // Source from shadow number or main line
    ivrChoice,        // IVR selection (service/sales)
    isReturningCaller, // Is this a returning caller
    existingCustomer,  // Existing customer from CRM
    connect,
    disconnect,
    makeCall,
    answerCall,
    rejectCall,
    hangup,
    toggleMute,
    toggleHold,
    sendDTMF,
    blindTransfer,
    openWithNumber,
    openCustomerCard,
    setIsOpen,
    setIsMinimized,
    setMatchedCustomer,
    setPendingNumber,
    callType,
    setCallType,
    currentEmotion,
    setCurrentEmotion,
    currentTip,
    setCurrentTip,
    emotionDataRef
  };

  return (
    <SoftphoneContext.Provider value={contextValue}>
      {children}
    </SoftphoneContext.Provider>
  );
};

/**
 * Softphone UI Component
 */
export const SoftphoneUI = () => {
  const softphone = useSoftphone();
  const [dialNumber, setDialNumber] = useState('');
  const [activeTab, setActiveTab] = useState('dial');
  const [showDtmfPad, setShowDtmfPad] = useState(false);
  const [recentCalls, setRecentCalls] = useState([]);
  const [extensions, setExtensions] = useState([]);
  const [showCallLinkModal, setShowCallLinkModal] = useState(false);
  const [callLink, setCallLink] = useState(null);
  const [creatingLink, setCreatingLink] = useState(false);

  const {
    regState = REG_STATE.DISCONNECTED,
    callState = CALL_STATE.IDLE,
    callInfo = null,
    customerInfo = null,
    error = null,
    isMuted = false,
    callDuration = 0,
    sipCredentials = null,
    isOpen = false,
    isMinimized = false,
    pendingNumber = null,
    makeCall = () => {},
    answerCall = () => {},
    rejectCall = () => {},
    hangup = () => {},
    toggleMute = () => {},
    toggleHold = () => {},
    sendDTMF = () => {},
    setIsOpen = () => {},
    setIsMinimized = () => {},
    setPendingNumber = () => {},
    callType = 'service',
    setCallType = () => {},
    currentEmotion = null,
    setCurrentEmotion = () => {},
    currentTip = null,
    setCurrentTip = () => {},
    emotionDataRef = { current: {} }
  } = softphone || {};

  // Load recent calls
  useEffect(() => {
    if (!softphone || !isOpen) return;

    const loadRecentCalls = async () => {
      try {
        const res = await fetch(`${TELEPHONY_API}/calls/recent?limit=10`);
        const data = await res.json();
        setRecentCalls(data.calls || []);
      } catch (err) {
        console.error('Error loading recent calls:', err);
      }
    };

    const loadExtensions = async () => {
      try {
        const res = await fetch(`${TELEPHONY_API}/extensions`);
        const data = await res.json();
        setExtensions(data.data || data.extensions || []);
      } catch (err) {
        console.error('Error loading extensions:', err);
      }
    };

    loadRecentCalls();
    loadExtensions();
  }, [isOpen, softphone]);

  // Handle pending number
  useEffect(() => {
    if (!softphone) return;
    if (pendingNumber && regState === REG_STATE.REGISTERED && callState === CALL_STATE.IDLE) {
      setDialNumber(pendingNumber);
      setPendingNumber(null);
    }
  }, [pendingNumber, regState, callState, setPendingNumber, softphone]);

  if (!softphone) return null;

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const handleDial = () => {
    if (dialNumber && regState === REG_STATE.REGISTERED && callState === CALL_STATE.IDLE) {
      makeCall(dialNumber);
      setDialNumber('');
    }
  };

  const handleDialPadClick = (digit) => {
    if (callState === CALL_STATE.ACTIVE) {
      sendDTMF(digit);
    } else {
      setDialNumber(prev => prev + digit);
    }
  };

  // Create WebRTC call link
  const createCallLink = async () => {
    setCreatingLink(true);
    try {
      const agentName = sipCredentials?.display_name || 'נציג';
      const agentExt = sipCredentials?.extension || '100';

      const res = await fetch(`${TELEPHONY_API}/call-rooms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agent_name: agentName,
          agent_extension: agentExt,
          customer_name: 'לקוח',
          customer_phone: dialNumber || null
        })
      });

      const data = await res.json();
      if (data.success) {
        setCallLink(data.room);
        setShowCallLinkModal(true);
      } else {
        alert('שגיאה ביצירת לינק: ' + (data.error || 'Unknown'));
      }
    } catch (err) {
      console.error('Error creating call link:', err);
      alert('שגיאה ביצירת לינק');
    } finally {
      setCreatingLink(false);
    }
  };

  // Copy link to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('הלינק הועתק!');
    }).catch(err => {
      console.error('Copy failed:', err);
    });
  };

  // Share via WhatsApp
  const shareViaWhatsApp = (link) => {
    const message = encodeURIComponent(`שלום, מזמין אותך להצטרף לשיחה: ${link}`);
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const getStatusInfo = () => {
    if (regState === REG_STATE.REGISTERED) {
      return { text: 'מחובר לשרת SIP', color: '#4caf50', icon: '🟢' };
    } else if (regState === REG_STATE.CONNECTING) {
      return { text: 'מתחבר...', color: '#ff9800', icon: '🟡' };
    } else if (regState === REG_STATE.FAILED) {
      return { text: 'חיבור נכשל', color: '#f44336', icon: '🔴' };
    } else {
      return { text: 'לא מחובר', color: '#9e9e9e', icon: '⚪' };
    }
  };

  const statusInfo = getStatusInfo();
  const isInCall = callState !== CALL_STATE.IDLE;

  if (!isOpen) return null;

  // Minimized view
  if (isMinimized && isInCall) {
    return (
      <div style={styles.minimizedBar}>
        <div style={styles.minimizedInfo}>
          <span style={styles.minimizedStatus}>
            {callState === CALL_STATE.ACTIVE ? '🎙️' : callState === CALL_STATE.INCOMING ? '📞' : '📱'}
          </span>
          <span style={styles.minimizedNumber}>
            {customerInfo?.name || formatPhone(callInfo?.remoteNumber)}
          </span>
          <span style={styles.minimizedDuration}>
            {callState === CALL_STATE.ACTIVE ? formatDuration(callDuration) : callState}
          </span>
        </div>
        <div style={styles.minimizedActions}>
          {callState === CALL_STATE.INCOMING && (
            <>
              <button style={{...styles.miniBtn, background: '#4caf50'}} onClick={answerCall}>📞</button>
              <button style={{...styles.miniBtn, background: '#f44336'}} onClick={hangup}>❌</button>
            </>
          )}
          {callState === CALL_STATE.ACTIVE && (
            <button style={{...styles.miniBtn, background: '#f44336'}} onClick={hangup}>📵</button>
          )}
          <button style={styles.miniBtn} onClick={() => setIsMinimized(false)}>⬆️</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <span style={styles.headerIcon}>📞</span>
            <span style={styles.headerTitle}>סופטפון</span>
            <span style={{...styles.statusBadge, background: statusInfo.color}}>
              {statusInfo.icon} {statusInfo.text}
            </span>
          </div>
          <div style={styles.headerActions}>
            {isInCall && (
              <button style={styles.headerBtn} onClick={() => setIsMinimized(true)} title="מזער">➖</button>
            )}
            <button style={styles.headerBtn} onClick={() => setIsOpen(false)} title="סגור">✕</button>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div style={styles.errorBanner}>⚠️ {error}</div>
        )}

        {/* Active/Incoming Call Panel */}
        {isInCall && (
          <div style={{
            ...styles.callPanel,
            background: callState === CALL_STATE.INCOMING
              ? 'linear-gradient(135deg, #ff5722 0%, #e64a19 100%)'
              : callState === CALL_STATE.ACTIVE
              ? 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)'
              : 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)'
          }}>
            <div style={styles.callInfo}>
              <div style={styles.callDirection}>
                {callState === CALL_STATE.INCOMING && '📞 שיחה נכנסת'}
                {callState === CALL_STATE.CONNECTING && '📱 מתחבר...'}
                {callState === CALL_STATE.RINGING && '🔔 מצלצל...'}
                {callState === CALL_STATE.ACTIVE && '🎙️ שיחה פעילה'}
                {callState === CALL_STATE.HOLDING && '⏸️ בהמתנה'}
              </div>
              <div style={styles.callNumber}>
                {customerInfo?.name || formatPhone(callInfo?.remoteNumber)}
              </div>
              {customerInfo && (
                <div style={styles.customerBadges}>
                  {customerInfo.company && <span style={styles.badge}>🏢 {customerInfo.company}</span>}
                  {customerInfo.type && <span style={styles.badge}>{customerInfo.type === 'lead' ? '🎯 ליד' : '👤 לקוח'}</span>}
                </div>
              )}
              {callState === CALL_STATE.ACTIVE && (
                <div style={styles.callTimer}>{formatDuration(callDuration)}</div>
              )}
            </div>

            <div style={styles.callActions}>
              {callState === CALL_STATE.INCOMING && (
                <>
                  <button style={{...styles.actionBtn, ...styles.answerBtn}} onClick={answerCall}>
                    📞<span style={styles.actionLabel}>ענה</span>
                  </button>
                  <button style={{...styles.actionBtn, ...styles.rejectBtn}} onClick={rejectCall}>
                    ❌<span style={styles.actionLabel}>דחה</span>
                  </button>
                </>
              )}
              {(callState === CALL_STATE.ACTIVE || callState === CALL_STATE.HOLDING) && (
                <>
                  <button
                    style={{...styles.actionBtn, ...(isMuted ? styles.activeBtn : styles.normalBtn)}}
                    onClick={toggleMute}
                  >
                    {isMuted ? '🔇' : '🎤'}<span style={styles.actionLabel}>{isMuted ? 'בטל השתקה' : 'השתק'}</span>
                  </button>
                  <button
                    style={{...styles.actionBtn, ...(callState === CALL_STATE.HOLDING ? styles.activeBtn : styles.normalBtn)}}
                    onClick={toggleHold}
                  >
                    {callState === CALL_STATE.HOLDING ? '▶️' : '⏸️'}
                    <span style={styles.actionLabel}>{callState === CALL_STATE.HOLDING ? 'חדש' : 'המתנה'}</span>
                  </button>
                  <button
                    style={{...styles.actionBtn, ...(showDtmfPad ? styles.activeBtn : styles.normalBtn)}}
                    onClick={() => setShowDtmfPad(!showDtmfPad)}
                  >
                    🔢<span style={styles.actionLabel}>מקשים</span>
                  </button>
                  <button style={{...styles.actionBtn, ...styles.hangupBtn}} onClick={hangup}>
                    📵<span style={styles.actionLabel}>נתק</span>
                  </button>
                </>
              )}
              {(callState === CALL_STATE.CONNECTING || callState === CALL_STATE.RINGING) && (
                <button style={{...styles.actionBtn, ...styles.hangupBtn}} onClick={hangup}>
                  ❌<span style={styles.actionLabel}>בטל</span>
                </button>
              )}
            </div>

            {showDtmfPad && callState === CALL_STATE.ACTIVE && (
              <div style={styles.dtmfPad}>
                {['1','2','3','4','5','6','7','8','9','*','0','#'].map(digit => (
                  <button key={digit} style={styles.dtmfBtn} onClick={() => sendDTMF(digit)}>
                    {digit}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* QSIS Emotion Analyzer - shown during active calls */}
        {callState === CALL_STATE.ACTIVE && (
          <div style={styles.emotionAnalyzerContainer}>
            <QSISEmotionAnalyzer
              isActive={true}
              callType={callType}
              onEmotionChange={setCurrentEmotion}
              onTipGenerated={setCurrentTip}
              emotionDataRef={emotionDataRef}
              compact={true}
            />
          </div>
        )}

        {/* Tabs - only when not in call */}
        {!isInCall && (
          <div style={styles.tabs}>
            <button
              style={{...styles.tab, ...(activeTab === 'dial' ? styles.tabActive : {})}}
              onClick={() => setActiveTab('dial')}
            >
              📞 חייגן
            </button>
            <button
              style={{...styles.tab, ...(activeTab === 'history' ? styles.tabActive : {})}}
              onClick={() => setActiveTab('history')}
            >
              📋 היסטוריה
            </button>
            <button
              style={{...styles.tab, ...(activeTab === 'extensions' ? styles.tabActive : {})}}
              onClick={() => setActiveTab('extensions')}
            >
              📱 שלוחות
            </button>
          </div>
        )}

        {/* Content */}
        <div style={styles.content}>
          {activeTab === 'dial' && !isInCall && (
            <div style={styles.dialTab}>
              {/* SIP Status */}
              <div style={{
                ...styles.sipStatus,
                background: regState === REG_STATE.REGISTERED ? '#e8f5e9' :
                           regState === REG_STATE.CONNECTING ? '#fff3e0' : '#ffebee'
              }}>
                <span style={styles.sipIcon}>{statusInfo.icon}</span>
                <div style={styles.sipText}>
                  <div style={styles.sipMain}>
                    {regState === REG_STATE.REGISTERED ? 'מוכן לשיחות WebRTC' :
                     regState === REG_STATE.CONNECTING ? 'מתחבר לשרת SIP...' :
                     regState === REG_STATE.FAILED ? 'חיבור נכשל' : 'לא מחובר'}
                  </div>
                  {sipCredentials?.extension && (
                    <div style={styles.sipSub}>שלוחה: {sipCredentials.extension}</div>
                  )}
                </div>
              </div>

              {/* Call Type Selector - QSIS */}
              <div style={styles.callTypeSection}>
                <CallTypeSelector value={callType} onChange={setCallType} />
              </div>

              {/* Dial Input */}
              <div style={styles.dialDisplay}>
                <input
                  type="tel"
                  value={dialNumber}
                  onChange={(e) => setDialNumber(e.target.value)}
                  placeholder="הקלד מספר טלפון..."
                  style={styles.dialInput}
                  dir="ltr"
                  onKeyPress={(e) => e.key === 'Enter' && handleDial()}
                />
                {dialNumber && (
                  <button style={styles.clearBtn} onClick={() => setDialNumber('')}>✕</button>
                )}
              </div>

              {/* Dial Pad - Standard phone layout (RTL order: 3,2,1) */}
              <div style={styles.dialPad}>
                {[
                  ['3', '2', '1'],
                  ['6', '5', '4'],
                  ['9', '8', '7'],
                  ['#', '0', '*']
                ].map((row, rowIndex) => (
                  row.map(digit => (
                    <button key={digit} style={styles.dialBtn} onClick={() => handleDialPadClick(digit)}>
                      <span style={styles.digitMain}>{digit}</span>
                      <span style={styles.digitSub}>
                        {digit === '2' ? 'ABC' : digit === '3' ? 'DEF' :
                         digit === '4' ? 'GHI' : digit === '5' ? 'JKL' :
                         digit === '6' ? 'MNO' : digit === '7' ? 'PQRS' :
                         digit === '8' ? 'TUV' : digit === '9' ? 'WXYZ' :
                         digit === '0' ? '+' : ''}
                      </span>
                    </button>
                  ))
                ))}
              </div>

              {/* Call Buttons */}
              <div style={styles.callButtonsRow}>
                <button
                  style={{
                    ...styles.callBtn,
                    opacity: dialNumber && regState === REG_STATE.REGISTERED ? 1 : 0.5
                  }}
                  onClick={handleDial}
                  disabled={!dialNumber || regState !== REG_STATE.REGISTERED}
                >
                  📞 התקשר
                </button>
                <button
                  style={styles.linkBtn}
                  onClick={createCallLink}
                  disabled={creatingLink}
                  title="צור לינק לשיחה בדפדפן"
                >
                  {creatingLink ? '...' : '🔗'}
                </button>
              </div>

              {/* Quick Dial */}
              {recentCalls.length > 0 && (
                <div style={styles.quickDial}>
                  <div style={styles.sectionTitle}>חיוג מהיר</div>
                  <div style={styles.quickGrid}>
                    {recentCalls.slice(0, 4).map((call, idx) => (
                      <button
                        key={idx}
                        style={styles.quickBtn}
                        onClick={() => makeCall(call.caller_number)}
                      >
                        <span>{call.direction === 'inbound' ? '📥' : '📤'}</span>
                        <span>{formatPhone(call.caller_number)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && !isInCall && (
            <div style={styles.historyTab}>
              <div style={styles.sectionTitle}>היסטוריית שיחות</div>
              {recentCalls.length > 0 ? (
                <div style={styles.historyList}>
                  {recentCalls.map((call, idx) => {
                    const phoneNumber = call.direction === 'inbound' ? call.caller_number : call.called_number;
                    const callDate = new Date(call.started_at);
                    const formattedDate = callDate.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: '2-digit' });
                    const formattedTime = callDate.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });

                    return (
                      <div
                        key={idx}
                        style={styles.historyItem}
                        onClick={() => {
                          setDialNumber(phoneNumber?.replace(/\D/g, '') || '');
                          setActiveTab('dial');
                        }}
                        title="לחץ להעברה לחייגן"
                      >
                        <span style={styles.historyIcon}>
                          {call.direction === 'inbound' ? '📥' : '📤'}
                        </span>
                        <div style={styles.historyInfo}>
                          {/* Account name if available */}
                          {call.account_name && (
                            <div style={styles.historyAccountName}>{call.account_name}</div>
                          )}
                          {/* Phone number */}
                          <div style={styles.historyNumber}>{formatPhone(phoneNumber)}</div>
                          {/* Date and time */}
                          <div style={styles.historyDateTime}>
                            <span>{formattedDate}</span>
                            <span style={styles.historyTimeSep}>|</span>
                            <span>{formattedTime}</span>
                            {call.agent_name && (
                              <>
                                <span style={styles.historyTimeSep}>|</span>
                                <span>👤 {call.agent_name}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <div style={styles.historyRight}>
                          <span style={{
                            ...styles.historyStatus,
                            color: call.status === 'completed' ? '#4caf50' :
                                   call.status === 'missed' ? '#f44336' : '#ff9800'
                          }}>
                            {call.status === 'completed' ? '✓' :
                             call.status === 'missed' ? '✗' : '○'}
                          </span>
                          <span style={styles.historyDuration}>
                            {formatDuration(call.talk_time || 0)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={styles.emptyState}>אין שיחות אחרונות</div>
              )}
            </div>
          )}

          {activeTab === 'extensions' && !isInCall && (
            <div style={styles.extensionsTab}>
              <div style={styles.sectionTitle}>שלוחות</div>
              {extensions.length > 0 ? (
                <div style={styles.extensionsList}>
                  {extensions.map((ext, idx) => (
                    <div
                      key={idx}
                      style={{
                        ...styles.extensionItem,
                        border: ext.extension_number === sipCredentials?.extension ? '2px solid #0176d3' : 'none'
                      }}
                      onClick={() => makeCall(ext.extension_number)}
                    >
                      <span style={styles.extIcon}>
                        {ext.type === 'queue' ? '🛎️' : ext.type === 'user' ? '👤' : '📞'}
                      </span>
                      <div style={styles.extInfo}>
                        <div style={styles.extName}>
                          {ext.name || `שלוחה ${ext.extension_number}`}
                          {ext.extension_number === sipCredentials?.extension && (
                            <span style={styles.myExtBadge}>שלוחה שלי</span>
                          )}
                        </div>
                        <div style={styles.extNumber}>שלוחה {ext.extension_number}</div>
                      </div>
                      <span style={{
                        ...styles.extStatus,
                        background: ext.status === 'online' ? '#4caf50' : '#ccc'
                      }}>
                        {ext.status === 'online' ? 'מחובר' : 'לא מחובר'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyState}>אין שלוחות מוגדרות</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Call Link Modal */}
      {showCallLinkModal && callLink && (
        <div style={styles.linkModalOverlay} onClick={() => setShowCallLinkModal(false)}>
          <div style={styles.linkModal} onClick={e => e.stopPropagation()}>
            <div style={styles.linkModalHeader}>
              <span>🔗 לינק לשיחה</span>
              <button style={styles.linkModalClose} onClick={() => setShowCallLinkModal(false)}>✕</button>
            </div>
            <div style={styles.linkModalBody}>
              <div style={styles.linkModalInfo}>
                <p>שלח את הלינק ללקוח כדי שיצטרף לשיחה בדפדפן.</p>
                <p style={styles.linkModalExpiry}>הלינק תקף ל-24 שעות</p>
              </div>

              <div style={styles.linkUrlBox}>
                <input
                  type="text"
                  readOnly
                  value={callLink.customer_link}
                  style={styles.linkInput}
                />
              </div>

              <div style={styles.linkModalActions}>
                <button
                  style={styles.copyBtn}
                  onClick={() => copyToClipboard(callLink.customer_link)}
                >
                  📋 העתק לינק
                </button>
                <button
                  style={styles.whatsappBtn}
                  onClick={() => shareViaWhatsApp(callLink.customer_link)}
                >
                  💬 שלח בוואטסאפ
                </button>
              </div>

              <div style={styles.linkModalJoin}>
                <button
                  style={styles.joinBtn}
                  onClick={() => window.open(callLink.agent_link, '_blank')}
                >
                  🎧 הצטרף לשיחה
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Floating Button for Softphone
 */
export const SoftphoneButton = () => {
  const softphone = useSoftphone();

  if (!softphone) return null;

  const { regState, callState, callInfo, isOpen, setIsOpen, setIsMinimized } = softphone;

  const isInCall = callState !== CALL_STATE.IDLE;
  const isRegistered = regState === REG_STATE.REGISTERED;

  const handleClick = () => {
    if (isOpen) {
      setIsMinimized(false);
    } else {
      setIsOpen(true);
    }
  };

  return (
    <button
      style={{
        ...styles.floatingBtn,
        animation: callState === CALL_STATE.INCOMING ? 'pulse 1s infinite' : 'none',
        background: isInCall
          ? 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)'
          : 'linear-gradient(135deg, #0176d3 0%, #1589ee 100%)'
      }}
      onClick={handleClick}
      title="סופטפון"
    >
      📞
      {isInCall && <span style={styles.activeDot} />}
      <span style={{
        ...styles.sipDot,
        background: isRegistered ? '#4caf50' : '#ff9800'
      }} />
    </button>
  );
};

/**
 * Incoming Call Popup - Shows when call arrives and softphone is minimized/closed
 */
// Format phone number: convert +972... or 972... to 0...
const formatPhoneNumber = (number) => {
  if (!number) return '';
  let formatted = number.replace(/^\+/, ''); // Remove + prefix
  if (formatted.startsWith('972')) {
    formatted = '0' + formatted.substring(3); // Replace 972 with 0
  }
  return formatted;
};

// BroadcastChannel for cross-tab communication
const callChannel = typeof BroadcastChannel !== 'undefined'
  ? new BroadcastChannel('afikim-incoming-call')
  : null;

// Request notification permission on load
if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
  Notification.requestPermission();
}

// Helper to format duration
const formatCallDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export const IncomingCallPopup = () => {
  const softphone = useSoftphone();
  const notificationRef = useRef(null);
  const lastCallIdRef = useRef(null);

  const callState = softphone?.callState;
  const callInfo = softphone?.callInfo;
  const customerInfo = softphone?.customerInfo;
  const matchedCustomer = softphone?.matchedCustomer;
  const callDuration = softphone?.callDuration || 0;
  const answerCall = softphone?.answerCall;
  const rejectCall = softphone?.rejectCall;
  const hangup = softphone?.hangup;
  const openCustomerCard = softphone?.openCustomerCard;
  const setMatchedCustomer = softphone?.setMatchedCustomer;
  const setIsOpen = softphone?.setIsOpen;
  const setIsMinimized = softphone?.setIsMinimized;
  // Lead info from telephony server
  const leadSource = softphone?.leadSource;
  const ivrChoice = softphone?.ivrChoice;
  const isReturningCaller = softphone?.isReturningCaller;
  const existingCustomer = softphone?.existingCustomer;

  // Check if we have multiple matches to display
  const hasMultipleMatches = matchedCustomer?.multiple && matchedCustomer?.matches?.length > 1;

  // Show popup for incoming call (both INCOMING and RINGING states for incoming direction)
  const isIncomingCall = callState === CALL_STATE.INCOMING ||
    (callState === CALL_STATE.RINGING && callInfo?.direction === 'incoming');

  // Show popup for active call too
  const isActiveCall = callState === CALL_STATE.ACTIVE;

  // Show popup for connecting state (after clicking answer, waiting for connection)
  const isConnecting = callState === CALL_STATE.CONNECTING && callInfo?.direction === 'incoming';

  // Show popup when there's an incoming, connecting, or active call
  const showPopup = isIncomingCall || isConnecting || isActiveCall;

  const displayNumber = customerInfo?.name || formatPhoneNumber(callInfo?.remoteNumber);

  // Show system notification for incoming calls
  useEffect(() => {
    if (!softphone) return;

    if (isIncomingCall && callInfo?.callId !== lastCallIdRef.current) {
      lastCallIdRef.current = callInfo?.callId;

      // Show browser notification (appears above all windows)
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        // Close previous notification if exists
        if (notificationRef.current) {
          notificationRef.current.close();
        }

        notificationRef.current = new Notification('שיחה נכנסת - אפיקים', {
          body: `${displayNumber}`,
          icon: '/logo192.png',
          tag: 'incoming-call',
          requireInteraction: true,
          silent: false
        });

        notificationRef.current.onclick = () => {
          window.focus();
          notificationRef.current.close();
        };
      }

      // Broadcast to other tabs
      if (callChannel) {
        callChannel.postMessage({
          type: 'incoming-call',
          callInfo,
          displayNumber
        });
      }

      console.log('[CallPopup] SHOWING POPUP + NOTIFICATION for incoming call!');
    }

    // Cleanup notification when call ends (but not when answered)
    if (!isIncomingCall && !isActiveCall && notificationRef.current) {
      notificationRef.current.close();
      notificationRef.current = null;
      lastCallIdRef.current = null;
    }

    // Close notification when call is answered
    if (isActiveCall && notificationRef.current) {
      notificationRef.current.close();
      notificationRef.current = null;
    }
  }, [softphone, isIncomingCall, isActiveCall, callInfo, displayNumber]);

  // Listen for cross-tab messages
  useEffect(() => {
    if (!callChannel) return;

    const handleMessage = (event) => {
      if (event.data.type === 'answer-call') {
        console.log('[CallPopup] Answer from another tab');
      } else if (event.data.type === 'reject-call') {
        console.log('[CallPopup] Reject from another tab');
      }
    };

    callChannel.addEventListener('message', handleMessage);
    return () => callChannel.removeEventListener('message', handleMessage);
  }, []);

  console.log('[CallPopup] callState:', callState, 'showPopup:', showPopup);

  if (!softphone || !showPopup) {
    return null;
  }

  const handleAnswer = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('[CallPopup] Answer button clicked!');

    // Close notification
    if (notificationRef.current) {
      notificationRef.current.close();
    }

    // Broadcast to other tabs
    if (callChannel) {
      callChannel.postMessage({ type: 'answer-call' });
    }

    setIsOpen(true);
    setIsMinimized(false);
    answerCall();
  };

  const handleReject = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('[CallPopup] Reject button clicked!');

    // Close notification
    if (notificationRef.current) {
      notificationRef.current.close();
    }

    // Broadcast to other tabs
    if (callChannel) {
      callChannel.postMessage({ type: 'reject-call' });
    }

    rejectCall();
  };

  const handleHangup = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('[CallPopup] Hangup button clicked!');
    hangup();
  };

  // Handle customer selection from multiple matches
  const handleSelectCustomer = (customer) => {
    console.log('[CallPopup] Selected customer:', customer.name);
    setMatchedCustomer(customer);
    openCustomerCard(customer);
  };

  // Use portal to render directly in body, avoiding any parent z-index issues
  return ReactDOM.createPortal(
    <>
      {/* Main Call Popup */}
      <div style={{
        ...styles.incomingPopup,
        border: isActiveCall ? '3px solid #4caf50' : isConnecting ? '3px solid #2196f3' : '3px solid #ff9800'
      }}>
        {/* Header - different for incoming vs connecting vs active */}
        <div style={{
          ...styles.incomingHeader,
          color: isActiveCall ? '#4caf50' : isConnecting ? '#2196f3' : '#ff9800'
        }}>
          {isActiveCall ? '🟢 שיחה פעילה' : isConnecting ? '⏳ מתחבר...' : '📞 שיחה נכנסת'}
        </div>

        {/* Phone number / Customer name */}
        <div style={styles.incomingNumber}>
          {displayNumber}
        </div>

        {/* Company name if available */}
        {customerInfo?.company && (
          <div style={styles.incomingCompany}>{customerInfo.company}</div>
        )}

        {/* Lead Source and IVR Choice badges */}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '8px' }}>
          {/* Existing customer badge */}
          {existingCustomer && (
            <span style={{
              background: '#e8f5e9',
              color: '#2e7d32',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              ✓ לקוח קיים: {existingCustomer.name}
            </span>
          )}
          {/* Returning caller badge */}
          {isReturningCaller && (
            <span style={{
              background: '#fff3e0',
              color: '#f57c00',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              ↺ מתקשר חוזר
            </span>
          )}
          {/* Lead source badge */}
          {leadSource && (
            <span style={{
              background: '#e3f2fd',
              color: '#1976d2',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              📍 {leadSource}
            </span>
          )}
          {/* IVR choice badge */}
          {ivrChoice && (
            <span style={{
              background: ivrChoice === 'שירות לקוחות' ? '#e8f5e9' : '#fff3e0',
              color: ivrChoice === 'שירות לקוחות' ? '#2e7d32' : '#f57c00',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              {ivrChoice === 'שירות לקוחות' ? '🛠️' : '💼'} {ivrChoice}
            </span>
          )}
        </div>

        {/* Call duration for active calls */}
        {isActiveCall && (
          <div style={{
            fontSize: '28px',
            fontWeight: 'bold',
            textAlign: 'center',
            color: '#4caf50',
            margin: '10px 0',
            fontFamily: 'monospace'
          }}>
            {formatCallDuration(callDuration)}
          </div>
        )}

        {/* Action buttons - ALWAYS visible and clickable */}
        <div style={styles.incomingActions}>
          {isIncomingCall ? (
            <>
              <button
                type="button"
                style={styles.incomingAnswer}
                onClick={handleAnswer}
              >
                ענה
              </button>
              <button
                type="button"
                style={styles.incomingReject}
                onClick={handleReject}
              >
                דחה
              </button>
            </>
          ) : isConnecting ? (
            <>
              <div style={{
                flex: 1,
                textAlign: 'center',
                color: '#2196f3',
                fontWeight: 'bold',
                padding: '12px',
                background: '#e3f2fd',
                borderRadius: '8px'
              }}>
                ⏳ ממתין לחיבור...
              </div>
              <button
                type="button"
                style={styles.incomingReject}
                onClick={handleHangup}
              >
                בטל
              </button>
            </>
          ) : (
            <button
              type="button"
              style={{
                ...styles.incomingReject,
                flex: 1
              }}
              onClick={handleHangup}
            >
              נתק שיחה
            </button>
          )}
        </div>
      </div>

      {/* Separate Customer List Panel - Below the call popup */}
      {hasMultipleMatches && (
        <div style={{
          position: 'fixed',
          top: '180px',
          right: '20px',
          width: '320px',
          maxHeight: '300px',
          overflowY: 'auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          zIndex: 2147483646,
          border: '2px solid #1976d2'
        }}>
          <div style={{
            padding: '12px',
            background: '#1976d2',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '14px',
            borderRadius: '10px 10px 0 0'
          }}>
            נמצאו {matchedCustomer.matches.length} לקוחות - בחר:
          </div>
          {matchedCustomer.matches.map((match, idx) => (
            <div
              key={match.id || idx}
              onClick={() => handleSelectCustomer(match)}
              style={{
                padding: '12px 15px',
                borderBottom: '1px solid #eee',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: 'white',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#e3f2fd'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
            >
              <div>
                <div style={{ fontWeight: '600', fontSize: '14px' }}>{match.name || '(ללא שם)'}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {match.type === 'Account' ? 'חשבון' : 'איש קשר'} | {match.phone || match.Another_phone__c || '-'}
                </div>
              </div>
              <span style={{ color: '#1976d2', fontSize: '20px' }}>←</span>
            </div>
          ))}
        </div>
      )}
    </>,
    document.body
  );
};

/**
 * Call Highlight Indicator - Highlights the customer tab and shows call timer
 * Red border during ringing, Green border during active call
 */
export const CallHighlightIndicator = () => {
  const softphone = useSoftphone();

  const callState = softphone?.callState;
  const callDuration = softphone?.callDuration || 0;
  const matchedCustomer = softphone?.matchedCustomer;

  const isRinging = callState === CALL_STATE.INCOMING || callState === CALL_STATE.RINGING;
  const isActive = callState === CALL_STATE.ACTIVE;
  // Only show highlight if we have a single selected customer (not multiple matches)
  const hasSingleCustomer = matchedCustomer && !matchedCustomer.multiple;
  const showHighlight = (isRinging || isActive) && hasSingleCustomer;

  // Inject dynamic CSS to highlight the page
  useEffect(() => {
    if (!showHighlight) {
      // Remove highlight styles
      const existingStyle = document.getElementById('call-highlight-style');
      if (existingStyle) existingStyle.remove();
      return;
    }

    const color = isActive ? '#4caf50' : '#f44336'; // Green for active, Red for ringing

    const css = `
      /* Highlight the main content area */
      .record-detail-container,
      .main-content,
      [class*="RecordDetail"] {
        box-shadow: inset 0 0 0 4px ${color} !important;
        transition: box-shadow 0.3s ease;
      }

      /* Pulsing animation for ringing */
      ${isRinging ? `
        @keyframes call-pulse {
          0%, 100% { box-shadow: inset 0 0 0 4px ${color}; }
          50% { box-shadow: inset 0 0 0 8px ${color}; }
        }
        .record-detail-container,
        .main-content,
        [class*="RecordDetail"] {
          animation: call-pulse 1s infinite !important;
        }
      ` : ''}
    `;

    let styleEl = document.getElementById('call-highlight-style');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'call-highlight-style';
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = css;

    return () => {
      // Cleanup on unmount
      const el = document.getElementById('call-highlight-style');
      if (el) el.remove();
    };
  }, [showHighlight, isActive, isRinging]);

  // Don't render anything if no highlight needed
  if (!showHighlight) return null;

  // Render floating timer badge on the page
  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed',
      top: '70px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: isActive ? '#4caf50' : '#f44336',
      color: 'white',
      padding: '8px 20px',
      borderRadius: '20px',
      fontSize: '16px',
      fontWeight: 'bold',
      fontFamily: 'monospace',
      zIndex: 99998,
      boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    }}>
      <span>{isActive ? '🟢' : '🔴'}</span>
      <span>{matchedCustomer?.name}</span>
      <span>|</span>
      <span>{formatCallDuration(callDuration)}</span>
    </div>,
    document.body
  );
};

// Styles
const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 99998,
    animation: 'fadeIn 0.2s ease'
  },
  modal: {
    width: '450px',
    maxWidth: '95vw',
    maxHeight: '90vh',
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    direction: 'rtl'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    background: 'linear-gradient(135deg, #0176d3 0%, #1589ee 100%)',
    color: 'white'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  headerIcon: {
    fontSize: '24px'
  },
  headerTitle: {
    fontSize: '18px',
    fontWeight: '600'
  },
  statusBadge: {
    fontSize: '11px',
    padding: '3px 8px',
    borderRadius: '10px',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  headerActions: {
    display: 'flex',
    gap: '8px'
  },
  headerBtn: {
    background: 'rgba(255,255,255,0.2)',
    border: 'none',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    color: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  errorBanner: {
    background: '#ffebee',
    color: '#c62828',
    padding: '10px 20px',
    fontSize: '14px',
    textAlign: 'center'
  },
  callPanel: {
    padding: '24px 20px',
    color: 'white'
  },
  callInfo: {
    textAlign: 'center',
    marginBottom: '20px'
  },
  callDirection: {
    fontSize: '14px',
    opacity: 0.9,
    marginBottom: '8px'
  },
  callNumber: {
    fontSize: '28px',
    fontWeight: '700',
    marginBottom: '8px'
  },
  customerBadges: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '12px'
  },
  badge: {
    background: 'rgba(255,255,255,0.2)',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px'
  },
  callTimer: {
    fontSize: '36px',
    fontWeight: '300',
    fontFamily: 'monospace'
  },
  callActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    flexWrap: 'wrap'
  },
  actionBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    padding: '12px 16px',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    fontSize: '24px',
    transition: 'all 0.2s',
    minWidth: '70px'
  },
  actionLabel: {
    fontSize: '11px',
    fontWeight: '500'
  },
  answerBtn: { background: '#4caf50', color: 'white' },
  rejectBtn: { background: '#f44336', color: 'white' },
  hangupBtn: { background: '#f44336', color: 'white' },
  normalBtn: { background: 'rgba(255,255,255,0.2)', color: 'white' },
  activeBtn: { background: '#ff9800', color: 'white' },
  dtmfPad: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '8px',
    maxWidth: '200px',
    margin: '20px auto 0'
  },
  dtmfBtn: {
    padding: '12px',
    border: 'none',
    borderRadius: '8px',
    background: 'rgba(255,255,255,0.2)',
    color: 'white',
    fontSize: '18px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  tabs: {
    display: 'flex',
    borderBottom: '1px solid #eee'
  },
  tab: {
    flex: 1,
    padding: '12px',
    border: 'none',
    background: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#666',
    transition: 'all 0.2s'
  },
  tabActive: {
    color: '#0176d3',
    borderBottom: '3px solid #0176d3',
    fontWeight: '600'
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '20px'
  },
  dialTab: {
    textAlign: 'center'
  },
  sipStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderRadius: '12px',
    marginBottom: '20px'
  },
  sipIcon: { fontSize: '24px' },
  sipText: { flex: 1, textAlign: 'right' },
  sipMain: { fontSize: '14px', fontWeight: '600', color: '#1a1a1a' },
  sipSub: { fontSize: '12px', color: '#666' },
  dialDisplay: {
    position: 'relative',
    marginBottom: '20px'
  },
  dialInput: {
    width: '100%',
    padding: '16px',
    fontSize: '24px',
    textAlign: 'center',
    border: '2px solid #eee',
    borderRadius: '12px',
    outline: 'none',
    fontFamily: 'monospace',
    boxSizing: 'border-box'
  },
  clearBtn: {
    position: 'absolute',
    left: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: '#f5f5f5',
    border: 'none',
    borderRadius: '50%',
    width: '28px',
    height: '28px',
    cursor: 'pointer'
  },
  dialPad: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '10px',
    maxWidth: '280px',
    margin: '0 auto 20px'
  },
  dialBtn: {
    width: '70px',
    height: '70px',
    borderRadius: '50%',
    border: 'none',
    background: '#f5f5f5',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  digitMain: { fontSize: '24px', fontWeight: '600', color: '#1a1a1a' },
  digitSub: { fontSize: '9px', color: '#666', letterSpacing: '1px' },
  callButtonsRow: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px'
  },
  callBtn: {
    flex: 1,
    padding: '16px',
    background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '18px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  linkBtn: {
    width: '50px',
    padding: '16px',
    background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '18px',
    cursor: 'pointer'
  },
  // Link Modal Styles
  linkModalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10001
  },
  linkModal: {
    background: 'white',
    borderRadius: '16px',
    width: '400px',
    maxWidth: '90vw',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
  },
  linkModalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #eee',
    fontSize: '18px',
    fontWeight: '600'
  },
  linkModalClose: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#666'
  },
  linkModalBody: {
    padding: '20px'
  },
  linkModalInfo: {
    marginBottom: '20px',
    textAlign: 'center',
    color: '#666'
  },
  linkModalExpiry: {
    fontSize: '12px',
    color: '#999',
    marginTop: '5px'
  },
  linkUrlBox: {
    marginBottom: '20px'
  },
  linkInput: {
    width: '100%',
    padding: '12px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    direction: 'ltr',
    textAlign: 'left',
    background: '#f5f5f5'
  },
  linkModalActions: {
    display: 'flex',
    gap: '10px',
    marginBottom: '15px'
  },
  copyBtn: {
    flex: 1,
    padding: '12px',
    background: '#2196f3',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600'
  },
  whatsappBtn: {
    flex: 1,
    padding: '12px',
    background: '#25d366',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600'
  },
  linkModalJoin: {
    borderTop: '1px solid #eee',
    paddingTop: '15px',
    textAlign: 'center'
  },
  joinBtn: {
    padding: '12px 30px',
    background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '16px'
  },
  quickDial: { marginTop: '20px', textAlign: 'right' },
  sectionTitle: { fontSize: '14px', color: '#666', marginBottom: '12px', fontWeight: '500' },
  quickGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' },
  quickBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    background: '#f8f9fa',
    border: '1px solid #eee',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  historyTab: { maxHeight: '400px', overflowY: 'auto' },
  historyList: {},
  historyItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 14px',
    background: '#f8f9fa',
    borderRadius: '10px',
    marginBottom: '8px',
    cursor: 'pointer',
    transition: 'background 0.2s, transform 0.1s',
    border: '1px solid transparent'
  },
  historyIcon: { fontSize: '20px', minWidth: '28px', textAlign: 'center' },
  historyInfo: { flex: 1, minWidth: 0 },
  historyAccountName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: '2px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  historyNumber: { fontSize: '13px', fontWeight: '500', color: '#444', direction: 'ltr', textAlign: 'right' },
  historyDateTime: {
    fontSize: '11px',
    color: '#888',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '4px',
    flexWrap: 'wrap'
  },
  historyTimeSep: { color: '#ccc' },
  historyRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '4px',
    minWidth: '50px'
  },
  historyStatus: { fontSize: '16px', fontWeight: 'bold' },
  historyDuration: { fontSize: '11px', color: '#666', fontFamily: 'monospace' },
  extensionsTab: {},
  extensionsList: {},
  extensionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px',
    background: '#f8f9fa',
    borderRadius: '10px',
    marginBottom: '8px',
    cursor: 'pointer'
  },
  extIcon: { fontSize: '24px' },
  extInfo: { flex: 1 },
  extName: { fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px' },
  myExtBadge: { fontSize: '10px', background: '#0176d3', color: 'white', padding: '2px 6px', borderRadius: '4px' },
  extNumber: { fontSize: '12px', color: '#666' },
  extStatus: { padding: '4px 10px', borderRadius: '12px', fontSize: '11px', color: 'white' },
  emptyState: { textAlign: 'center', padding: '40px', color: '#666' },
  minimizedBar: {
    position: 'fixed',
    bottom: '20px',
    left: '80px',
    right: '20px',
    background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
    borderRadius: '12px',
    padding: '12px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
    zIndex: 99997,
    direction: 'rtl',
    color: 'white'
  },
  minimizedInfo: { display: 'flex', alignItems: 'center', gap: '12px' },
  minimizedStatus: { fontSize: '20px' },
  minimizedNumber: { fontSize: '16px', fontWeight: '600' },
  minimizedDuration: { fontSize: '14px', opacity: 0.9, fontFamily: 'monospace' },
  minimizedActions: { display: 'flex', gap: '8px' },
  miniBtn: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(255,255,255,0.2)',
    cursor: 'pointer',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  floatingBtn: {
    position: 'fixed',
    bottom: '80px',
    left: '20px',
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    border: 'none',
    boxShadow: '0 4px 20px rgba(1, 118, 211, 0.4)',
    cursor: 'pointer',
    fontSize: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 99990,
    transition: 'transform 0.2s'
  },
  activeDot: {
    position: 'absolute',
    bottom: '2px',
    right: '2px',
    width: '14px',
    height: '14px',
    background: '#4caf50',
    borderRadius: '50%',
    border: '2px solid white'
  },
  sipDot: {
    position: 'absolute',
    top: '2px',
    left: '2px',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    border: '2px solid white'
  },
  incomingPopup: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
    border: '3px solid #4caf50',
    zIndex: 2147483647,
    padding: '16px',
    direction: 'rtl',
    width: '280px',
    pointerEvents: 'auto'
  },
  incomingHeader: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '8px',
    textAlign: 'center'
  },
  incomingNumber: {
    fontSize: '24px',
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: '4px'
  },
  incomingCompany: {
    fontSize: '14px',
    color: '#666',
    textAlign: 'center',
    marginBottom: '16px'
  },
  incomingActions: {
    display: 'flex',
    gap: '10px',
    marginTop: '12px'
  },
  incomingAnswer: {
    flex: 1,
    padding: '12px 16px',
    background: '#4caf50',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    pointerEvents: 'auto',
    position: 'relative',
    zIndex: 2147483647
  },
  incomingReject: {
    flex: 1,
    padding: '12px 16px',
    background: '#f44336',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    pointerEvents: 'auto',
    position: 'relative',
    zIndex: 2147483647
  },
  // QSIS Emotion Analyzer Styles
  callTypeSection: {
    padding: '8px 0',
    marginBottom: '8px'
  },
  emotionAnalyzerContainer: {
    padding: '12px 16px',
    background: '#f8f9fa',
    borderTop: '1px solid #eee'
  }
};

// Add CSS animations
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.1); }
    }
    @keyframes slideIn {
      from { transform: translateX(100%); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
  `;
  document.head.appendChild(styleSheet);
}

export default SoftphoneProvider;
